import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
    Page,
    LayoutSingleColumn,
    LayoutWrapperTopbar,
    LayoutWrapperMain,
    LayoutWrapperFooter,
    CreditsPurchasingModal,
    Button,
    Footer,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { ensureCurrentUser } from '../../util/data';

import css from './BalancePage.css';
import { CREDITS_ACTION_REFERRAL_INVITER } from '../../credits-config';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { checkIfCreditsEventExists } from '../../util/credits';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import CreditsPurchaseHistory from './CreditsPurchaseHistory';
import { useInitialPackageFromSearchParam } from './useInitialPackageFromSearchParam';

export const SEARCH_PARAM_PACKAGE_KEY = 'pckg';

export const BalancePage = ({
    scrollingDisabled,
    currentUser,
    getCurrentUser,
    location,
    history,
}) => {
    const [modalOpen, setModalOpen] = useState(false);

    const selectedPackage = useInitialPackageFromSearchParam({ setModalOpen });
    const { paymentError, navigateToErrorPage, navigateFromErrorPage } = usePaymentStatus({
        location,
        history,
        pageName: 'BalancePage',
    });

    const schemaTitle = 'Deine Bilanz';
    const schemaDescription = 'Hier findest Du die Gesamtzahl und Historie deiner Credits.';

    const {
        privateData: { creditsAvailable = {}, creditsHistory },
        publicData: { userType },
    } = currentUser.attributes.profile;

    const creditsForUserType = creditsHistory && creditsHistory[userType];
    const creditsHistoryAvailable =
        !paymentError && Array.isArray(creditsForUserType) && creditsForUserType.length > 0
            ? creditsForUserType
            : null;

    const showReferralBlock = !checkIfCreditsEventExists(
        currentUser,
        CREDITS_ACTION_REFERRAL_INVITER
    );

    useEffect(() => {
        getCurrentUser();
    }, []);

    const navigateTo = pageName =>
        history.push(createResourceLocatorString(pageName, routeConfiguration(), {}, {}));

    return (
        <Page
            scrollingDisabled={scrollingDisabled}
            contentType="website"
            description={schemaDescription}
            title={schemaTitle}
            schema={{
                '@context': 'http://schema.org',
                '@type': 'WebPage',
                description: schemaDescription,
                name: schemaTitle,
                image: ['https://horsedeal.imgix.net/static/social-image-1200x630.webp'],
            }}
        >
            <LayoutSingleColumn>
                <LayoutWrapperTopbar>
                    <TopbarContainer currentPage="BalancePage" />
                </LayoutWrapperTopbar>
                <LayoutWrapperMain
                    className={css.layout}
                    blobBackground
                    blobProps={{
                        xDistance: 20,
                        baseWidth: 760,
                    }}
                >
                    <main className={css.container}>
                        <header className={css.header}>
                            <h1 className={css.heading}>Credits</h1>
                            <p className={css.subHeading}>Deine Bilanz</p>
                        </header>
                        <div className={css.boxesHolder}>
                            <div className={classNames(css.creditsPurchaseBox, css.box)}>
                                <h2 className={css.creditsPurchaseHeading}>
                                    <span>{creditsAvailable[userType] || 0} Credits</span>
                                    <p
                                        className={css.supportLink}
                                        onClick={() => {
                                            window.open(
                                                'https://support.horsedeal.com/hc/de/articles/18591062923538',
                                                '_blank'
                                            );
                                        }}
                                    >
                                        Was sind Credits?
                                    </p>
                                </h2>
                                <Button onClick={() => setModalOpen(true)} className={css.cta}>
                                    Credits hinzufügen
                                </Button>
                            </div>
                            <div className={classNames(css.creditsReferralBox, css.box)}>
                                <p>Täglich Credits & Preise gewinnen</p>
                                <p>
                                    Decke Karten auf und sichere dir kostenlose Credits und
                                    Marken-Preise.
                                </p>
                                <Button
                                    onClick={() => navigateTo('FlipAndWin')}
                                    className={css.cta}
                                >
                                    Karten aufdecken
                                </Button>
                            </div>
                            {showReferralBlock && (
                                <div className={classNames(css.creditsReferralBox, css.box)}>
                                    <p>100 Credits verschenken - 50 Credits erhalten</p>
                                    <p>
                                        Lade Freund:innen ein und sichere dir 50 Credits, wenn sie
                                        sich registrieren.
                                    </p>
                                    <Button
                                        onClick={() => navigateTo('ReferralPage')}
                                        className={css.cta}
                                    >
                                        Freunde einladen
                                    </Button>
                                </div>
                            )}
                        </div>
                        {creditsHistoryAvailable ? (
                            <CreditsPurchaseHistory
                                creditsHistoryAvailable={creditsHistoryAvailable}
                            />
                        ) : (
                            <>
                                <p className={css.error}>
                                    {paymentError
                                        ? 'Zahlung fehlgeschlagen. Bitte versuche es später erneut.'
                                        : 'Die Historie deiner Credits ist momentan nicht verfügbar.'}
                                </p>
                                <Button type="button" onClick={() => navigateFromErrorPage()}>
                                    Zurück
                                </Button>
                            </>
                        )}
                        {modalOpen && (
                            <CreditsPurchasingModal
                                isOpen
                                currentUser={currentUser}
                                initialPackage={selectedPackage}
                                onClose={() => setModalOpen(false)}
                                onError={() => {
                                    setModalOpen(false);
                                    navigateToErrorPage();
                                }}
                            />
                        )}
                    </main>
                </LayoutWrapperMain>
                <LayoutWrapperFooter>
                    <Footer />
                </LayoutWrapperFooter>
            </LayoutSingleColumn>
        </Page>
    );
};

const mapStateToProps = ({ UI, user: { currentUser } }) => ({
    scrollingDisabled: isScrollingDisabled({ UI }),
    currentUser: ensureCurrentUser(currentUser),
});

const mapDispatchToProps = dispatch => ({
    getCurrentUser: () => dispatch(fetchCurrentUser()),
});

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(BalancePage);
